const API_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787';

interface RequestOptions extends RequestInit {
  token?: string | undefined;
  workspaceId?: string | undefined;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { token, workspaceId, ...init } = options;

  const headers: Record<string, string> = {
    ...(!(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(workspaceId ? { 'X-Workspace-Id': workspaceId } : {}),
  };

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { message?: string; code?: string };
    };
    const err = new ApiError(
      body.error?.code ?? 'REQUEST_FAILED',
      body.error?.message ?? `Request failed: ${res.status}`,
      res.status,
    );
    throw err;
  }

  const body = (await res.json()) as { data: T };
  return body.data;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'GET' }),

  post: <T>(path: string, data: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body: JSON.stringify(data) }),

  patch: <T>(path: string, data: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body: JSON.stringify(data) }),

  delete: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};
